// Persists in-progress mixing job uploads to IndexedDB so a client can resume
// after a crash, tab close, or logout — without re-selecting files that
// haven't finished uploading yet. File bytes are kept only for files that
// aren't fully uploaded; once a file (or multipart part) is confirmed done,
// its blob is dropped and only lightweight metadata remains.
//
// Degrades silently to a no-op (resume simply unavailable) when IndexedDB
// isn't present — older browsers, some private-browsing modes, and the
// jsdom test environment all lack a global `indexedDB`.

var DB_NAME = 'profdebeats-uploads'
var DB_VERSION = 1
var META_STORE = 'draftMeta'
var BLOB_STORE = 'draftBlobs'

var _supported = typeof indexedDB !== 'undefined'
var _warned = false
var _dbPromise = null

function warnUnsupported () {
  if (!_warned) {
    _warned = true
    console.warn('uploadDraftService: IndexedDB unavailable — upload resume is disabled for this session.')
  }
}

function promisifyRequest (request) {
  return new Promise(function (resolve, reject) {
    request.onsuccess = function () { resolve(request.result) }
    request.onerror = function () { reject(request.error) }
  })
}

function openDb () {
  if (!_supported) return Promise.resolve(null)
  if (_dbPromise) return _dbPromise

  _dbPromise = new Promise(function (resolve, reject) {
    var req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = function () {
      var db = req.result
      if (!db.objectStoreNames.contains(META_STORE)) {
        var metaStore = db.createObjectStore(META_STORE, { keyPath: 'draftId' })
        metaStore.createIndex('email', 'email', { unique: false })
      }
      if (!db.objectStoreNames.contains(BLOB_STORE)) {
        db.createObjectStore(BLOB_STORE, { keyPath: 'key' })
      }
    }
    req.onsuccess = function () { resolve(req.result) }
    req.onerror = function () {
      warnUnsupported()
      resolve(null)
    }
  })

  return _dbPromise
}

function withStore (storeName, mode, fn) {
  return openDb().then(function (db) {
    if (!db) {
      warnUnsupported()
      return null
    }
    var tx = db.transaction(storeName, mode)
    var store = tx.objectStore(storeName)
    return fn(store)
  }).catch(function (err) {
    warnUnsupported()
    return null
  })
}

function blobKey (draftId, fingerprint) {
  return draftId + '::' + fingerprint
}

/**
 * Stable identity for a file, independent of File object identity.
 * @param {File} file
 * @returns {string}
 */
export function fingerprint (file) {
  return file.name + '|' + file.size + '|' + (file.lastModified || 0)
}

function generateDraftId () {
  return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10)
}

/**
 * Rough guard against writing more blob bytes than the browser has room for.
 * Unsupported/unknown quota APIs are treated as "assume it's fine" — this is
 * a safety net, not a hard guarantee.
 * @param {number} totalBytes
 * @returns {Promise<boolean>}
 */
function hasEnoughQuota (totalBytes) {
  if (!navigator.storage || !navigator.storage.estimate) return Promise.resolve(true)
  return navigator.storage.estimate().then(function (estimate) {
    var available = (estimate.quota || 0) - (estimate.usage || 0)
    if (!estimate.quota) return true
    return available > totalBytes * 1.1 // small safety margin
  }).catch(function () { return true })
}

/**
 * Create a new draft record and stash blobs for every file (subject to quota).
 * @param {{ email: string, form: object, files: Array<{ file: File, isMultipart: boolean }> }} payload
 * @returns {Promise<{ draftId: string, blobsStored: boolean }>}
 */
export function createDraft (payload) {
  if (!_supported) return Promise.resolve({ draftId: null, blobsStored: false })

  var draftId = generateDraftId()
  var now = Date.now()
  var totalBytes = payload.files.reduce(function (sum, f) { return sum + f.file.size }, 0)

  return hasEnoughQuota(totalBytes).then(function (storeBlobsOk) {
    var fileRecords = payload.files.map(function (f) {
      return {
        fingerprint: fingerprint(f.file),
        name: f.file.name,
        size: f.file.size,
        type: f.file.type || '',
        status: 'pending',
        isMultipart: !!f.isMultipart,
        objectKey: null,
        uploadId: null,
        totalParts: null,
        completedParts: []
      }
    })

    var meta = {
      draftId: draftId,
      email: payload.email,
      createdAt: now,
      updatedAt: now,
      form: payload.form,
      files: fileRecords
    }

    return withStore(META_STORE, 'readwrite', function (store) {
      return promisifyRequest(store.put(meta))
    }).then(function () {
      if (!storeBlobsOk) return { draftId: draftId, blobsStored: false }

      return withStore(BLOB_STORE, 'readwrite', function (store) {
        payload.files.forEach(function (f) {
          store.put({ key: blobKey(draftId, fingerprint(f.file)), blob: f.file })
        })
        return true
      }).then(function () {
        return { draftId: draftId, blobsStored: true }
      })
    })
  })
}

function summarize (meta) {
  var total = meta.files.length
  var done = meta.files.filter(function (f) { return f.status === 'done' }).length
  return {
    draftId: meta.draftId,
    projectName: meta.form.projectName,
    jobType: meta.form.jobType,
    totalFiles: total,
    doneFiles: done,
    updatedAt: meta.updatedAt
  }
}

/**
 * Most recently updated, not-yet-complete draft belonging to this user.
 * @param {string} email
 * @returns {Promise<{draftId,projectName,jobType,totalFiles,doneFiles,updatedAt}|null>}
 */
export function getDraftForUser (email) {
  if (!email) return Promise.resolve(null)

  return withStore(META_STORE, 'readonly', function (store) {
    var index = store.index('email')
    return promisifyRequest(index.getAll(email))
  }).then(function (records) {
    if (!records || !records.length) return null
    var incomplete = records.filter(function (m) {
      return m.files.some(function (f) { return f.status !== 'done' })
    })
    if (!incomplete.length) return null
    incomplete.sort(function (a, b) { return b.updatedAt - a.updatedAt })
    return summarize(incomplete[0])
  })
}

/**
 * Full metadata for a draft (form + per-file progress), no blobs.
 * @param {string} draftId
 */
export function getDraftDetail (draftId) {
  return withStore(META_STORE, 'readonly', function (store) {
    return promisifyRequest(store.get(draftId))
  })
}

/**
 * The stored blob for a file that hasn't finished uploading yet.
 * @param {string} draftId
 * @param {string} fileFingerprint
 * @returns {Promise<Blob|null>}
 */
export function getFileBlob (draftId, fileFingerprint) {
  return withStore(BLOB_STORE, 'readonly', function (store) {
    return promisifyRequest(store.get(blobKey(draftId, fileFingerprint)))
  }).then(function (record) {
    return record ? record.blob : null
  })
}

/**
 * Merge progress fields into a file's record within a draft.
 * @param {string} draftId
 * @param {string} fileFingerprint
 * @param {object} patch  e.g. { status: 'done', objectKey } or
 *                         { uploadId, objectKey, totalParts, completedParts }
 */
export function updateFileProgress (draftId, fileFingerprint, patch) {
  return withStore(META_STORE, 'readwrite', function (store) {
    return promisifyRequest(store.get(draftId)).then(function (meta) {
      if (!meta) return null
      var target = meta.files.find(function (f) { return f.fingerprint === fileFingerprint })
      if (!target) return null
      Object.assign(target, patch)
      meta.updatedAt = Date.now()
      return promisifyRequest(store.put(meta))
    })
  })
}

/**
 * Drop a file's stored bytes once it's fully uploaded — the data now lives
 * durably in MinIO, so keeping a local copy just wastes browser storage.
 * @param {string} draftId
 * @param {string} fileFingerprint
 */
export function dropFileBlob (draftId, fileFingerprint) {
  return withStore(BLOB_STORE, 'readwrite', function (store) {
    return promisifyRequest(store.delete(blobKey(draftId, fileFingerprint)))
  })
}

/**
 * Remove a draft entirely — its metadata and any remaining blobs.
 * @param {string} draftId
 */
export function deleteDraft (draftId) {
  return withStore(META_STORE, 'readwrite', function (store) {
    return promisifyRequest(store.get(draftId)).then(function (meta) {
      var deleteMeta = promisifyRequest(store.delete(draftId))
      if (!meta) return deleteMeta
      return deleteMeta.then(function () { return meta })
    })
  }).then(function (meta) {
    if (!meta) return null
    return withStore(BLOB_STORE, 'readwrite', function (store) {
      meta.files.forEach(function (f) {
        store.delete(blobKey(draftId, f.fingerprint))
      })
      return true
    })
  })
}
